/* eslint-disable tailwindcss/no-arbitrary-value */
/* eslint-disable id-length */
import React, { useMemo, useEffect, useCallback } from 'react';
import { useBreakpoint } from '@one-thd/sui-atomic-components';
import _some from 'lodash/some';
import { useDesignStore } from '../../store/design/context';
import {
  useConfiguratorStore,
  useMediaStore,
  useProductInfoStore,
  useProductDataStore,
  useDigitalDataStore,
  useDropZoneStore,
} from '../../store';
import { Draggable } from '../Draggable';
import { Droppable } from '../Droppable';
import DropzoneArrowIcon from './components/DropzoneArrow';
import { calculate3DDropZones, getProductTypeFromMetadata, parseGridSpan } from './utils/index';
import { groupProductsByType } from './utils/grouping/groupProductsByType';
import { getBundleSkusAndPrice } from '../../utils/bundle-utils';
import logNewRelicCustom from '../../utils/logNewRelicCustom';
import { NEW_RELIC_EVENTS } from '../../constants';
import { computeDraggabilityMap } from './utils/draggability/index';
import {
  buildHandleIndexing,
  buildHandleStackGroups,
  calculateHandleStackHeight,
  getHandleDraggabilityState,
  getHandleEligibleDropZones,
  HANDLE_AREA,
  HANDLE_BASE_CHOICE_ID,
  isHandleStarterChoiceId,
} from './utils/handleRules';
import { useClickAway } from '../../hooks/useClickAway';

const MAIN_AREA = 'main';

const splitStackColumns = (products = []) => ({
  leftProducts: products.filter((product) => product.Origin[1] < 2),
  rightProducts: products.filter((product) => product.Origin[1] >= 2)
});

const StackInner = () => {
  const matches = useBreakpoint('md');
  const isDesktop = matches.greater;
  const design = useDesignStore((state) => state.design);
  const stackLayoutDirection = useProductInfoStore(
    (state) => state.productInfo?.stackLayoutDirection || 'right'
  );
  const metadata = useConfiguratorStore((state) => state.configurator.metadata);
  const media = useMediaStore((state) => state.assets);
  const selectVisualizerItem = useDesignStore((state) => state.selectVisualizerItem);
  const addProduct = useDesignStore((state) => state.add);
  const addBundleProducts = useDesignStore((state) => state.addBundleProducts);
  const productData = useProductDataStore((state) => state.productData);
  const bundles = useDesignStore((state) => state.bundles);
  const trackAddItemToStackEvent = useDigitalDataStore((state) => state.trackAddItemToStackEvent);
  const mainProducts = useMemo(
    () => (design.products || []).filter((product) => product?.targetArea !== HANDLE_AREA),
    [design.products]
  );
  const handleProducts = useMemo(
    () => (design.products || []).filter((product) => product?.targetArea === HANDLE_AREA),
    [design.products]
  );
  const isInitial = mainProducts.length < 1;
  const selectedVisualizerItemStackId = design?.selectedVisualizerItemStackId;
  const dropZones = useDropZoneStore((state) => state.dropZones);
  const addDropZones = useDropZoneStore((state) => state.addDropZones);
  const resetDropZones = useDropZoneStore((state) => state.resetDropZones);
  const resetSelected = useDesignStore((state) => state.resetSelected);

  const selectedProductType = design.selectedProduct
    ? getProductTypeFromMetadata(metadata?.[design.selectedProduct.choiceId]?.metadata)
    : null;

  useEffect(() => {
    const zones = selectedProductType
      ? calculate3DDropZones(selectedProductType, mainProducts, metadata, isInitial)
      : [];
    addDropZones(zones);
  }, [design.selectedProduct, mainProducts, metadata, isInitial]);

  const productGroupsByType = groupProductsByType(mainProducts, metadata);

  const addProductToDesign = (choiceId, choiceMetadata, zone, tubeSpan, targetArea = MAIN_AREA) => {
    const {
      BundleDesign: bundleDesign = null,
      Sku: sku = null
    } = choiceMetadata;

    if (bundleDesign) {
      addBundleProducts(metadata, choiceId, bundleDesign, zone, mainProducts, targetArea);
      const skus = bundles?.data?.[bundleDesign]?.bundleSkus;
      const bundleSkusAndPrices = getBundleSkusAndPrice(
        skus,
        productData
      );
      return trackAddItemToStackEvent(bundleSkusAndPrices);
    }

    addProduct(choiceId, choiceMetadata, [...zone, tubeSpan], targetArea);
    return trackAddItemToStackEvent(sku, productData?.[sku]?.pricing?.value);
  };

  const logChoiceAddition = async (choiceId, choiceMetadata) => {
    logNewRelicCustom(NEW_RELIC_EVENTS.CLICK_ADD_CHOICE, {
      choiceId,
      ...choiceMetadata
    });
  };

  const handlePlace3D = (x, y, _z, _id, targetArea = MAIN_AREA) => {
    if (!design.selectedProduct) return;

    const selectedProductChoiceId = design.selectedProduct.choiceId;
    if (targetArea === HANDLE_AREA && selectedProductType === 'full') {
      return;
    }
    if (
      targetArea === HANDLE_AREA
      && handleProducts.length === 0
      && !isHandleStarterChoiceId(selectedProductChoiceId)
    ) {
      return;
    }
    const choiceMetadata = metadata?.[selectedProductChoiceId]?.metadata;
    const { height } = parseGridSpan(choiceMetadata?.GridSpan);
    const placementZone = [y, x];

    // Add to design.products
    addProductToDesign(selectedProductChoiceId, choiceMetadata, placementZone, height, targetArea);
    logChoiceAddition(selectedProductChoiceId, choiceMetadata);
    resetDropZones();
  };
  const handleClick = (stackItemId, isDraggable) => {
    if (isDraggable && stackItemId) {
      selectVisualizerItem(stackItemId, resetDropZones);
    }
  };
  const draggabilityMap = useMemo(
    () => computeDraggabilityMap(
      { products: mainProducts },
      productGroupsByType,
      metadata,
      { alwaysLockBaseFull: false }
    ),
    [mainProducts, productGroupsByType, metadata]
  );

  const isHandleBasePresent = _some(
    mainProducts,
    (product) => product?.choiceId === HANDLE_BASE_CHOICE_ID
  );
  const selectedChoiceId = design?.selectedProduct?.choiceId;
  const canStartHandleStack = isHandleStarterChoiceId(selectedChoiceId);
  const isHandleTypeAllowed = selectedProductType !== 'full';
  const shouldShowHandleStack = handleProducts.length > 0;
  const shouldShowFirstHandleDropzone = !shouldShowHandleStack
    && canStartHandleStack
    && selectedProductType === 'wide';
  const shouldShowHandleDropzone = isHandleBasePresent
    && isHandleTypeAllowed
    && (shouldShowHandleStack || shouldShowFirstHandleDropzone);

  // This is the handle part: keep all handle-only indexing and constraints together.
  const firstHandleProduct = handleProducts[0] || null;
  const firstHandleProductImmediateAbove = handleProducts[1] || null;
  const handleIndexing = useMemo(
    () => buildHandleIndexing(handleProducts, metadata),
    [handleProducts, metadata]
  );

  const getProductHeight = useCallback(
    (choiceId) => Number(metadata?.[choiceId]?.metadata?.ProductStackHeight) || 10,
    [metadata]
  );

  const handleDropZones = useMemo(() => {
    return getHandleEligibleDropZones({
      shouldShowHandleDropzone,
      shouldShowHandleStack,
      dropZones,
      handleProducts,
      handleIndexing,
      getProductHeight
    });
  }, [shouldShowHandleDropzone, shouldShowHandleStack, dropZones, handleProducts, handleIndexing, getProductHeight]);
  const handleStackGroups = useMemo(() => {
    return buildHandleStackGroups(handleProducts, handleIndexing);
  }, [handleProducts, handleIndexing]);
  const handleStackHeight = useMemo(() => {
    return calculateHandleStackHeight(handleStackGroups, getProductHeight);
  }, [handleStackGroups, getProductHeight]);

  const handleDropzoneDynamicStyle = useMemo(() => {
    if (!handleStackHeight) return {};
    const bottomCalc = `calc(-1rem + ${handleStackHeight}px)`;
    return {
      bottom: bottomCalc
    };
  }, [handleStackHeight]);

  const getHandleDraggability = (product) => {
    const baseDraggable = draggabilityMap?.[product.stackItemId] ?? true;
    return getHandleDraggabilityState({
      product,
      baseDraggable,
      handleIndexing,
      firstHandleProduct,
      firstHandleProductImmediateAbove
    });
  };

  const renderDropZones = (zones, area = MAIN_AREA) => {
    const dropzoneType = getProductTypeFromMetadata(
      metadata?.[design.selectedProduct?.choiceId]?.metadata
    );

    return zones.map((zone, index) => {
      const classZoneId = zone.id;
      const droppableId = area === 'handle' ? `${zone.id}-handle` : zone.id;
      const zoneKey = `${droppableId}-${index}`;
      const isHiddenHandleRearZone = area === HANDLE_AREA && zone?.y === 0;

      return zone.isBlocked ? (
        <div
          key={`${zoneKey}-blocked`}
          className={
            `child-dropzone-blocked${dropzoneType === 'wide' ? ' child-dropzone-blocked-wide' : ''}`
          }
          style={isHiddenHandleRearZone ? { visibility: 'hidden' } : undefined}
        />
      ) : (
        <React.Fragment key={zoneKey}>
          <div style={isHiddenHandleRearZone ? { visibility: 'hidden' } : undefined}>
            <DropzoneArrowIcon type={classZoneId} />
          </div>
          <div
            className={`parent-${classZoneId} dropzone-box-target-${classZoneId} dropzone-box-target-visible`}
            role="button"
            tabIndex={0}
            onClick={() => {
              if (isHiddenHandleRearZone) return;
              handlePlace3D(zone.x, zone.y, zone.z, zone.id, area);
            }}
            onKeyDown={(e) => {
              if (isHiddenHandleRearZone) return;
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handlePlace3D(zone.x, zone.y, zone.z, zone.id, area);
              }
            }}
            title="Click to place product here"
            type="button"
            aria-label={`Drop zone for ${dropzoneType} product at position ${zone.x}, ${zone.y}, ${zone.z}`}
            style={isHiddenHandleRearZone ? { visibility: 'hidden', pointerEvents: 'none' } : undefined}
          >
            {isHiddenHandleRearZone ? (
              <div
                role="button"
                aria-label={`child-${classZoneId}`}
                tabIndex={0}
                className={`child-${classZoneId} dropzone-global`}
              />
            ) : (
              <Droppable id={droppableId} key={`${droppableId}-${index}`} origin={[zone.y, zone.x]}>
                <div
                  role="button"
                  aria-label={`child-${classZoneId}`}
                  tabIndex={0}
                  className={`child-${classZoneId} dropzone-global`}
                />
              </Droppable>
            )}
          </div>
        </React.Fragment>
      );
    });
  };

  const renderProduct = (product, index, isDraggableOverride) => {
    const productMetadata = metadata?.[product.choiceId]?.metadata;
    let height = productMetadata?.ProductStackHeight || 10;
    let width = Number(productMetadata?.ProductBaseWidth) || (product.type === 'wide' ? 200 : 125);
    let right = Number(productMetadata?.Right);
    let bottom = Number(productMetadata?.Bottom);
    let imagePosition = {};

    if (product.choiceId === 257615) { // ridgid organizer
      imagePosition = { width: 140 };
    }
    if (product.choiceId === 275425
      || product.choiceId === 279622
      || product.choiceId === 275395
      || product.choiceId === 275403
      || product.choiceId === 275405
      || product.choiceId === 275406
      || product.choiceId === 275424
    ) { // ryobi
      imagePosition = { width: 135 };
    }

    const isDraggable = isDraggableOverride !== undefined
      ? isDraggableOverride
      : true;
    return (
      <div
        style={{
          border: 'none',
          height: Number(height),
          width: width || 200,
          zIndex: index + 1,
          right: right || 'auto',
          bottom: bottom || 'auto',
          ...(selectedVisualizerItemStackId === product.stackItemId && {
            filter: 'drop-shadow(0 0 0.75rem lime)',
            backdropFilter: 'blur(0)',
            transform: 'translate3d(0, 0, 0)',
          })
        }}
        className="stack-item"
        data-sku={product?.Sku}
        data-choice-id={product?.choiceId}
        role="button"
        onClick={() => handleClick(product.stackItemId, isDraggable)}
        tabIndex={0}
      >
        <Draggable
          key={product.stackItemId}
          id={product.stackItemId}
          isVisualizerDrag
          isDraggable={isDraggable}
          choiceId={product.choiceId}
          bundleId={product?.choiceBundle?.id}
          imagePosition={imagePosition}
        >
          <img
            style={{ border: 'none' }}
            alt={`single-stack-${product.stackItemId}`}
            src={media?.[product.choiceId]?.mediaUrl}
            className="stack-item-image"
            height="auto"
            width="auto"
          />
        </Draggable>
      </div>

    );
  };

  const renderStackGroup = (stackGroup, groupIndex, keyPrefix, resolveDraggable) => {
    if (stackGroup.type === 'multi') {
      const { leftProducts, rightProducts } = splitStackColumns(stackGroup.products);
      return (
        <div className="multi-stack" style={{ zIndex: groupIndex + 1 }} key={`${keyPrefix}-multi-${groupIndex}`}>
          <div className="left-stack">
            {leftProducts.length > 0 && (
              leftProducts.map(
                (product, i) => renderProduct(product, i, resolveDraggable(product))
              ).reverse()
            )}
          </div>
          <div className="right-stack">
            {rightProducts.length > 0 && (
              rightProducts.map(
                (product, i) => renderProduct(product, i, resolveDraggable(product))
              ).reverse()
            )}
          </div>
        </div>
      );
    }

    if (stackGroup.type === 'single') {
      return (
        <div className="single-stack" style={{ zIndex: groupIndex + 1 }} key={`${keyPrefix}-single-${groupIndex}`}>
          {stackGroup.products.map(
            (product, i) => renderProduct(product, i, resolveDraggable(product))
          ).reverse()}
        </div>
      );
    }

    return null;
  };

  const resolveMainDraggability = (product) => draggabilityMap[product.stackItemId];

  const renderStacksInOrder = (group, section) => {
    const stackGroups = group[section].stackGroups || [];
    return stackGroups.map(
      (stackGroup, groupIndex) => renderStackGroup(stackGroup, groupIndex, section, resolveMainDraggability)
    );
  };

  const ref = useClickAway(() => {
    resetSelected();
    resetDropZones();
  });

  let multiStackLeft = '7%';
  const shouldMoveLeft = stackLayoutDirection === 'left';
  if (!isDesktop) {
    multiStackLeft = shouldMoveLeft ? '-14%' : '14%';
  } else if (shouldMoveLeft) {
    multiStackLeft = '-7%';
  }

  return (
    <div className="stack-inner-wrapper">
      <div className="stack-inner">
        <div
          className="stack-container"
          style={productGroupsByType[0]?.type === 'multi-stack' ? { left: multiStackLeft } : {}}
          ref={ref}
        >
          {/* Render 3D Drop Zones */}
          <div className="dropzone-box dropzone-box-main">
            <div className="sui-align-center">
              <div className="sui-h-full sui-z-[99] sui-relative sui-flex sui-flex-col">
                {renderDropZones(dropZones)}
              </div>
            </div>
          </div>
          {shouldShowHandleDropzone && handleDropZones.length > 0 && (
            <div className="dropzone-box dropzone-box-handle" style={handleDropzoneDynamicStyle}>
              <div className="sui-align-center">
                <div className="sui-h-full sui-z-[99] sui-relative sui-flex sui-flex-col">
                  {renderDropZones(handleDropZones, 'handle')}
                </div>
              </div>
            </div>
          )}
          {shouldShowHandleStack && (
            <div className="handle-stack-container">
              <div className="handle-stack">
                {handleStackGroups.map(
                  (group, groupIndex) => renderStackGroup(group, groupIndex, 'handle', getHandleDraggability)
                ).reverse()}
              </div>
            </div>
          )}
          <>
            {productGroupsByType.map((group, index) => {
              if (group.type === 'multi-stack') {
                return (
                  <div className="default-container" style={{ zIndex: index }} key={`multi-stack-${index}`}>
                    <div key={`multi-stack-${index}`} className="multi-stack-container">
                      {/* Front Section with dynamic ordering */}
                      <div name="Front" className="front">
                        {renderStacksInOrder(group, 'front').reverse()}
                      </div>

                      {/* Rear Section with dynamic ordering */}
                      <div name="Rear" className="rear">
                        {renderStacksInOrder(group, 'rear').reverse()}
                      </div>
                    </div>
                  </div>
                );
              } if (group.type === 'full') {
                return (
                  <div key={`full-${index}`} className="sui-relative" style={{ zIndex: index }}>
                    {renderProduct(group.product, 0, draggabilityMap[group.product.stackItemId])}
                  </div>
                );
              }
              return null;
            }).reverse()}
          </>
        </div>
      </div>
    </div>
  );
};

export default StackInner;
